import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import debounce from 'lodash/debounce';

import AccountsRegularSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/AccountsRebrandRegularSVG';
import ChevronRightLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/ChevronRightLightSVG';
import Input from '@jobcase/react_pattern_library/components/Input/InputRebrand';
import Link, { PREV_LINK_STYLES } from '@jobcase/react_pattern_library/components/Link/LinkRebrand';
import SearchLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/SearchLightSVG';
import Shimmer from '@jobcase/react_pattern_library/components/Shimmer';
import SVGIcon from '@jobcase/react_pattern_library/components/SVGIcon';
import TimesLightSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/TimesRebrandLightSVG';
import Typography from '@jobcase/react_pattern_library/components/Typography/TypographyRebrand';
import styles from '@jobcase/react_pattern_library/styles/rebrand';

import {
  getCurrentEmployer,
  getCurrentSubEmployer,
  getHasMultipleEmployers,
  getJobcaseEmployerAdminLevel,
} from '../../../redux/nodes/features/employers/selectors';
import { getEmployerById } from '../../../redux/nodes/entities/employers/selectors';
import { getIsLoading as getIsHiringEventsLoading } from '../../../redux/nodes/entities/hiringEvents/selectors';
import { getOrganizationAdminDetails } from '../../../redux/nodes/features/settings/actions';
import { getOrganizationsAdminDetails, isAdminOrganizationLoading } from '../../../redux/nodes/features/settings/selectors';
import employerActions from '../../../redux/nodes/entities/employers/actions';

import { ORGANIZATION_STATUS_COLOR_MAP, ORGANIZATION_STATUS_READABLE_MAP } from '../../../utils/constants/settings';
import { toSentenceCase } from '../../../utils/utils';
import { TRANSITION_DELAY } from '../../../utils/constants/employerNavigation';
import { useSelector } from '../../../utils/hooks';
import { getSubOrgById } from '../../../redux/nodes/entities/subOrganizations/selectors';
import config from '../../../config';

const SecondaryHeaderWrapper = styled.div`
  align-items: center;
  background-color: ${styles.color.jobcaseWhiteColor};
  border-bottom: 1px solid ${styles.color.dividerGrayColor};
  display: flex;
  height: 60px;
  padding: ${styles.spacing.extraExtraTiny}px ${styles.spacing.tiny}px;
`;

const SVGCloseIcon = styled(SVGIcon)`
  cursor: pointer;
  font-size: ${styles.font.fontSize.h2};
  margin-left: ${styles.spacing.extraTiny - 2}px;
  width: ${styles.spacing.tiny + 4}px;

  path {
    fill: ${styles.color.jobcaseBlackColor};
  }
`;

const NavSpaceHolder = styled.div`
  width: 20px;

  @media (min-width: ${styles.breakpoint.md}) {
    display: none;
  }
`;

const StyledLink = styled(Link).attrs({ linkStyle: PREV_LINK_STYLES.NO_UNDERLINE })`
  align-items: center;
  border: none;
  display: flex;
  font-weight: ${({ bold }) => (bold ? '500' : '400')};
  max-height: 74px;
  min-height: 60px;
  min-width: 240px;
  padding: 0 ${styles.spacing.small + 2}px 0 ${styles.spacing.tiny}px;
  position: relative;
  text-decoration: none;
  transition: height ${TRANSITION_DELAY}s;

  ${({ isSelected }) => isSelected && css`
    background-color: ${styles.color.commentBoxGrayColor};

    &:before {
      background-color: ${styles.color.primaryColor};
    }
  `}

  &:before {
    bottom: 0;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 4px;
  }

  &:hover {
    background-color: ${styles.color.commentBoxGrayColor};
  }

  @media(min-width: ${styles.breakpoint.md}) {
    padding: ${styles.spacing.tiny + 2}px ${styles.spacing.tiny}px;
  }
`;

const SearchSVGIcon = styled(SVGIcon)`
  height: 17px;
  margin-top: -2px;
  position: relative;
  top: 5px;
  width: 17px;
`;

const StyledTypography = styled(Typography)`
  color: ${styles.color.jobcaseBlackColor};
  font-family: ${styles.font.fontFamilyTitle};
  font-size: ${styles.font.fontSize.h3};
  font-weight: 500;
  line-height: ${styles.font.lineHeight.body};
  padding: ${styles.spacing.small}px ${styles.spacing.tiny}px;
`;

const StyledInfoTypography = styled(Typography)`
  font-weight: 600;
  padding: ${styles.spacing.tiny}px;
  text-align: center;
  width: 100%;
`;

const AccountIcon = styled(AccountsRegularSVG)`
  height: ${styles.spacing.tiny}px;
  width: ${styles.spacing.tiny}px;
`;

const StyledHeaderLink = styled(Link).attrs({ linkStyle: PREV_LINK_STYLES.NO_UNDERLINE })`
  align-items: center;
  border-bottom: 1px solid ${styles.color.dividerGrayColor};
  display: flex;
  font-weight: ${({ bold }) => (bold ? '500' : '400')};
  min-height: 60px;
  padding: 0 ${styles.spacing.small + 2}px 0 ${styles.spacing.tiny}px;
  position: relative;
  text-decoration: none;
  transition: background ${TRANSITION_DELAY}s;

  ${({ isSelected }) => isSelected && css`
    background-color: ${styles.color.commentBoxGrayColor};

    &:before {
      background-color: ${styles.color.primaryColor};
    }
  `}

  &:before {
    bottom: 0;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: ${styles.spacing.extraExtraTiny};
  }

  &:hover {
    background-color: ${styles.color.commentBoxGrayColor};
  }

  @media(min-width: ${styles.breakpoint.md}) {
    padding: 0 ${styles.spacing.tiny}px;
  }
`;

const SVGNextIcon = styled(SVGIcon)`
  cursor: pointer;
  font-size: 20px;
  position: absolute;
  right: ${styles.spacing.tiny}px;
  top: ${styles.spacing.tiny + 4}px;
  width: ${styles.spacing.extraTiny + 2}px;

  path {
    fill: ${styles.color.jobcaseBlackColor};
  }
`;

const SubNavLinkDropdown = styled.div`
  background-color: ${styles.color.jobcaseWhiteColor};
  display: ${({ isSideBarCollapsed }) => (isSideBarCollapsed ? 'none' : 'block')};
  height: 100%;
  left: ${({ isOpen }) => (isOpen ? '0' : '-100%')};
  position: absolute;
  top: 0;
  transition: left 0.3s;
  width: 100%;
  z-index: 2;

  [class*="InputRebrand__FormFieldWrapper"] {
    margin-bottom: ${styles.spacing.tiny}px;
  }
`;

const StyledInput = styled(Input)`
  margin: 0 ${styles.spacing.tiny}px;

  & > input {
    background-color: ${styles.color.jobcaseWhiteColor};
    border: 1px solid ${styles.color.strokeLineGrayColor};
    color: ${styles.color.jobcaseBlackColor};

    &::placeholder {
      color: ${styles.color.inputPlaceholder};
    }
  }

  & > input:focus ~ div svg path {
    fill: inherit;
  }
`;

const StyledAccountsList = styled.div`
  height: calc(100vh - 198px);
  overflow-y: scroll;
`;

const OrgStatus = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor || styles.color.commentBoxGrayColor)};
  border-radius: ${styles.spacing.borderRadius}px;
  color: ${styles.color.jobcaseBlackColor};
  font-family: ${styles.font.fontFamily};
  font-size: ${styles.font.fontSize.body2};
  font-weight: 400;
  line-height: ${styles.font.lineHeight.body2};
  margin-bottom: ${styles.spacing.extraExtraTiny}px;
  padding: ${styles.spacing.extraExtraTiny}px ${styles.spacing.extraTiny}px;
  width: fit-content;
`;

const InternalOrgNameTypography = styled(Typography)`
  color: ${styles.color.textGrayColor};
  display: block;
  margin-top: ${styles.spacing.extraExtraTiny}px;
`;

const ShimmerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${styles.spacing.extraExtraTiny}px;
  padding: ${styles.spacing.tiny}px;
`;

const EmployerDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${styles.spacing.tiny}px;
  position: relative;

  ${({ isSelected }) => isSelected && css`
    background-color: ${styles.color.commentBoxGrayColor};

    &:before {
      background-color: ${styles.color.primaryColor};
      bottom: 0;
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 4px;
    }
  `}

  &:hover {
    background-color: ${styles.color.commentBoxGrayColor};
  }

  &, & * {
    cursor: pointer;
  }
`;

const ListItemTypography = styled(Typography)`
  cursor: pointer;

  && {
    font-weight: 600;
  }
`;

const NavIcon = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-right: ${styles.spacing.tiny}px;

  svg {
    margin: 0;
    width: 24px;

    path {
      fill: ${styles.color.jobcaseBlackColor};
    }
  }
`;

const NavLink = styled.div`
  color: ${styles.color.jobcaseBlackColor};
  font-weight: 600;
  line-height: ${styles.font.lineHeight.body2};
`;

const MobileEmployerSwitcher = ({
  currentEmployerSelected,
  employers,
  handleClose,
  onChange,
  isOpen,
}) => {
  const dispatch = useDispatch();
  const fetchSearchResultsRef = useRef();

  const currentEmployerId = useSelector(getCurrentEmployer);
  const currentSubEmployerId = useSelector(getCurrentSubEmployer);
  const currentEmployer = useSelector(getEmployerById(currentEmployerId));
  const currentSubEmployer = useSelector(getSubOrgById(currentSubEmployerId));

  const jobcaseEmployerAdminLevel = useSelector(getJobcaseEmployerAdminLevel);
  const orgAdminIsLoading = useSelector(isAdminOrganizationLoading);
  const organizationsAdminDetails = useSelector(getOrganizationsAdminDetails);
  const hasMultipleEmployers = useSelector(getHasMultipleEmployers);
  const isHiringEventsLoading = useSelector(getIsHiringEventsLoading);

  const { environment } = config;

  const employersToShow = employers[0]?.id === currentEmployerId ? [employers[0]] : [employers[0], currentEmployer];

  const [filteredEmployers, setFilteredEmployers] = useState(jobcaseEmployerAdminLevel ? employersToShow : employers);
  const [searchQuery, setSearchQuery] = useState('');
  const [navDropdownOpen, setNavDropdownOpen] = useState(false);
  const [areEmployersLoading, setAreEmployersLoading] = useState(false);

  const showMultipleEmployers = jobcaseEmployerAdminLevel || hasMultipleEmployers;
  const isSwitcherDisabled = !showMultipleEmployers || isHiringEventsLoading;
  const areOrgDetailsLoading = orgAdminIsLoading || areEmployersLoading;

  const toggleNavDropdownOpen = () => setNavDropdownOpen(!navDropdownOpen);

  const switchEmployerAccount = (employer) => {
    onChange(employer, () => {
      setSearchQuery('');
      setFilteredEmployers(jobcaseEmployerAdminLevel ? employersToShow : employers);
      toggleNavDropdownOpen();
    });
  };

  const onSearchHandler = async ({ target: { value } }) => {
    setSearchQuery(value);
    if (!jobcaseEmployerAdminLevel) {
      const query = value.toLowerCase().trim();
      const result = employers.filter(employer => employer?.name.toLowerCase().includes(query)) || [];
      setFilteredEmployers(result);
    }
    if (fetchSearchResultsRef.current) {
      fetchSearchResultsRef.current.cancel();
    }
    fetchSearchResultsRef.current = debounce(async () => {
      const query = value.toLowerCase().trim();
      if (query.length >= 3) {
        setAreEmployersLoading(true);

        const queryParams = {
          name: query,
          sort_field: 'status',
          desc_sort: false,
        };

        // TODO: Remove override for production once sub-org feature is ready to be fully released.
        if (environment !== 'production') {
          queryParams.include_sub_org = true;
        }

        const result = await dispatch(employerActions.getEmployersByName(queryParams));
        if (result?.data?.length < 1) {
          setFilteredEmployers([]);
          setAreEmployersLoading(false);
          return;
        }
        setFilteredEmployers(result.data);
      } else {
        setFilteredEmployers(employersToShow);
      }
      setAreEmployersLoading(false);
    }, 500);
    if (jobcaseEmployerAdminLevel) fetchSearchResultsRef.current();
  };

  const resetQuery = () => {
    setSearchQuery('');
    setFilteredEmployers(jobcaseEmployerAdminLevel ? employersToShow : employers);
  };

  const handleBack = () => {
    resetQuery();
    toggleNavDropdownOpen();
  };

  useEffect(() => {
    if (jobcaseEmployerAdminLevel) {
      Promise.all(
        filteredEmployers
          .filter(employer => !organizationsAdminDetails?.[employer.id])
          .map(employer => dispatch(getOrganizationAdminDetails({ organizationId: employer.id }, true))),
      );
    }
  }, [filteredEmployers, jobcaseEmployerAdminLevel]);

  useEffect(() => {
    if (navDropdownOpen) toggleNavDropdownOpen();
  }, [isOpen]);

  // Enhance the result to include all matching sub-organizations belonging to the same organization.
  const expandedFilteredEmployers = useMemo(() => filteredEmployers.flatMap((employer) => {
    if (employer?.sub_orgs?.length && searchQuery?.length >= 3) {
      const expandedEmployers = [];
      if (employer?.name?.toLowerCase().includes(searchQuery?.toLowerCase())) {
        expandedEmployers.push({ ...employer });
      }

      employer.sub_orgs.forEach((subEmployer) => {
        expandedEmployers.push(
          {
            ...employer,
            subEmployer,
          },
        );
      });

      return expandedEmployers;
    }

    // Display selected sub-employer as an option if less than 3 characters are typed
    if (employer?.sub_orgs?.length && currentSubEmployerId) {
      const subEmployer = employer.sub_orgs.find(s => s?.id === currentSubEmployerId);

      return [{
        ...employer,
        subEmployer,
      }];
    }

    if (currentSubEmployer?.organization_id === employer?.id) {
      return [{
        ...employer,
        subEmployer: currentSubEmployer,
      }];
    }

    return [employer];
  }), [filteredEmployers, searchQuery, currentSubEmployerId]);

  return (
    <>
      <SecondaryHeaderWrapper>
        <SVGCloseIcon
          svgChild={TimesLightSVG}
          onClick={handleClose}
          color={styles.color.textGrayColor}
          size={SVGIcon.ICON_SIZE.X1}
        />
        <NavSpaceHolder />
      </SecondaryHeaderWrapper>
      <StyledLink
        onClick={showMultipleEmployers ? toggleNavDropdownOpen : null}
        href={null}
        isDisabled={isSwitcherDisabled}
      >
        <NavIcon>
          <SVGIcon
            svgChild={AccountIcon}
            color={styles.color.textGrayColor}
          />
        </NavIcon>
        <NavLink>
          {`Account: ${currentSubEmployer?.name ? `Sub Org: ${currentSubEmployer?.name}` : currentEmployer?.name}`}
        </NavLink>
        {!isSwitcherDisabled && (
        <SVGNextIcon
          svgChild={ChevronRightLightSVG}
          color={styles.color.textGrayColor}
          size={SVGIcon.ICON_SIZE.X1}
        />
        )}
      </StyledLink>
      <SubNavLinkDropdown isOpen={navDropdownOpen}>
        <StyledHeaderLink
          onClick={handleBack}
          href={null}
        >
          <SVGCloseIcon
            svgChild={TimesLightSVG}
            color={styles.color.jobcaseBlackColor}
            size={SVGIcon.ICON_SIZE.X1}
          />
        </StyledHeaderLink>
        <StyledTypography
          variant={Typography.VARIANTS.H2}
        >
          Choose an account
        </StyledTypography>
        <StyledInput
          placeholder="Search accounts"
          value={searchQuery}
          onChange={onSearchHandler}
          iconFront={(
            <SearchSVGIcon
              svgChild={SearchLightSVG}
              color={styles.color.bodyTextColor}
            />
          )}
        />
        <StyledAccountsList>
          {(expandedFilteredEmployers.length > 0) ? expandedFilteredEmployers.map((employer) => {
            const isSubOrg = !!(employer?.subEmployer?.id);
            const employerId = employer?.id;
            const name = isSubOrg ? employer?.subEmployer?.name : employer?.name;
            const key = `${employerId}-${employer?.subEmployer?.id}`;

            const status = organizationsAdminDetails?.[employerId]?.status;
            const internalOrgName = organizationsAdminDetails?.[employerId]?.internal_organization_name;

            return !areOrgDetailsLoading ? (
              <EmployerDetailsContainer
                onClick={() => switchEmployerAccount(employer)}
                isSelected={currentEmployerSelected === employerId}
                href={null}
                key={key}
              >
                {(status && jobcaseEmployerAdminLevel) && (
                  <OrgStatus backgroundColor={ORGANIZATION_STATUS_COLOR_MAP[status]}>
                    {ORGANIZATION_STATUS_READABLE_MAP[status] || toSentenceCase(status)}
                  </OrgStatus>
                )}
                <ListItemTypography key={key}>
                  {isSubOrg ? 'Sub Org: ' : ''}
                  {name}
                </ListItemTypography>
                {(internalOrgName && jobcaseEmployerAdminLevel) && (
                  <InternalOrgNameTypography variant={Typography.VARIANTS.BODY2}>
                    {internalOrgName}
                  </InternalOrgNameTypography>
                )}
              </EmployerDetailsContainer>
            ) : (
              <ShimmerContainer>
                <Shimmer height="28px" width="60px" />
                <Shimmer height="24px" width="190px" />
                <Shimmer height="20px" width="140px" />
              </ShimmerContainer>
            );
          }) : (
            <StyledInfoTypography>
              No results have been found for this search
            </StyledInfoTypography>
          )}
        </StyledAccountsList>
      </SubNavLinkDropdown>
    </>
  );
};

MobileEmployerSwitcher.propTypes = {
  currentEmployerSelected: PropTypes.string,
  employers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  handleClose: PropTypes.func,
  onChange: PropTypes.func,
  isOpen: PropTypes.bool,
};

MobileEmployerSwitcher.defaultProps = {
  currentEmployerSelected: null,
  employers: [],
  handleClose: () => {},
  onChange: () => {},
  isOpen: false,
};

export default MobileEmployerSwitcher;
